import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useRecoilValue } from "recoil";

import { ClipContext } from "../@types";
import { TClip } from "MVVM/@types";
import { CLIPS_RECOMMENDED, CLIPS_PROFILE } from "MVVM/GraphQL/feed";
import { PinnedClipsAtom } from "MVVM/State/clip";

const limit = 20;

interface FeedData {
  clipsRecommended: TClip[];
}

interface UserClipsData {
  getUser: {
    clips: {
      nodes: TClip[];
    };
  };
}

interface RecommendedClipsData {
  clipsRecommended: TClip[];
}

const FeedViewModel = (context?: ClipContext) => {
  const pinnedClips = useRecoilValue(PinnedClipsAtom);

  const queryConfig = useMemo(() => {
    switch (context?.type) {
      case "pinned-clips":
        return {
          query: CLIPS_RECOMMENDED,
          variables: { limit },
          dataTransform: (): FeedData => ({
            clipsRecommended:
              pinnedClips
                ?.slice()
                .reverse()
                .map(
                  (clip) =>
                    ({
                      ...clip,
                      _id: clip.id,
                      clipLink: `/clip/${clip.shareId}`,
                    }) as unknown as TClip,
                ) || [],
          }),
          skipQuery: true,
        };
      case "profile":
        return {
          query: CLIPS_PROFILE,
          variables: {
            first: limit,
            userId: context.metadata?.userId,
            filters: {},
          },
          dataTransform: (data: UserClipsData | undefined): FeedData => ({
            clipsRecommended: data?.getUser?.clips?.nodes || [],
          }),
        };
      default:
        return {
          query: CLIPS_RECOMMENDED,
          variables: { limit },
          dataTransform: (data: RecommendedClipsData | undefined): FeedData =>
            data || { clipsRecommended: [] },
        };
    }
  }, [context, pinnedClips]);

  const shouldSkipQuery = queryConfig.skipQuery;

  const { data, error, loading } = useQuery(queryConfig.query, {
    variables: queryConfig.variables,
    fetchPolicy: "cache-and-network",
    skip: shouldSkipQuery,
  });

  if (shouldSkipQuery) {
    return {
      data: queryConfig.dataTransform(),
      error: null,
      loading: false,
    };
  }

  return {
    data: queryConfig.dataTransform(data),
    error,
    loading,
  };
};

export { FeedViewModel };
